/*
 * File: AuthStore.js
 * Project: patient-portal
 *
 * Created by Nate Weber on 08/19/2020
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Store for user authentication
 */

import { decorate, flow, observable } from "mobx";
import { login, logout, getPortalUser } from "patient-portal-api-requests";
import PortalUserModel from "./models/PortalUserModel";

class AuthStore {
  error;
  portalUser;

  // Authenticate the user using the provided credentials
  authenticate = flow(function*(username, password) {
    this.error = undefined;

    if (username !== "" && password !== "") {
      try {
        yield login(username, password);
        yield this.fetchUser();
      } catch (error) {
        this.error = "Incorrect username or password.";
      }
    } else {
      this.error = "Incorrect username or password.";
    }
  });

  fetchUser = flow(function*() {
    try {
      const payload = this.decodeJWT();
      const portalResp = yield getPortalUser(payload.userId);
      this.portalUser = new PortalUserModel(portalResp);
    } catch (error) {
      this.error = "Failed to load authenticated user.";
    }
  });

  logout = flow(function*() {
    yield logout();
    window.sessionStorage.removeItem("authToken");
    this.portalUser = undefined;
  });

  // Check if the use is authenticated
  isAuthenticated() {
    const payload = this.decodeJWT();
    if (!payload) return false;

    const expiresAt = new Date(payload.exp * 1000);
    return expiresAt > new Date();
  }

  // Retrieve the token from the browser's local storage
  getJWT() {
    return window.sessionStorage.getItem("authToken");
  }

  // Decode JWT and return the payload
  decodeJWT() {
    const token = this.getJWT();
    if (!token) return null;

    const parts = token.split(".");
    return parts.length > 1 ? JSON.parse(atob(parts[1])) : null;
  }
}

decorate(AuthStore, {
  error: observable,
  portalUser: observable
});

export default AuthStore;
